'use client'

import { useCookieConsent } from '@xhubio-saas/core-frontend/kernel'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@xhubio-saas/core-frontend/ui-components'
import { formatDate, useLocale } from '@xhubio-saas/country-specifics-frontend'
import { useTranslation } from 'react-i18next'

/**
 * Cookie Policy page displaying all cookie categories and their details.
 *
 * This page provides transparency about what cookies are used,
 * their purpose, and how users can manage their preferences.
 */
export function CookiePolicyPage() {
  const { t, i18n } = useTranslation()
  const { categories, openSettings } = useCookieConsent()
  const locale = useLocale()

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('cookiePolicy.title', 'Cookie Policy')}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {t('cookiePolicy.lastUpdated', 'Last updated')}:{' '}
          {formatDate(new Date().toISOString(), { locale, withTime: false })}
        </p>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Introduction */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t('cookiePolicy.whatAreCookies.title', 'What are cookies?')}
          </h2>
          <p className="text-muted-foreground">
            {t(
              'cookiePolicy.whatAreCookies.description',
              'Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.'
            )}
          </p>
        </section>

        {/* Cookie Categories */}
        <section className="space-y-6">
          <h2 className="text-lg font-semibold">
            {t('cookiePolicy.cookiesWeUse.title', 'Cookies we use')}
          </h2>

          {categories.map((category) => (
            <div key={category.id} className="border rounded-lg p-4 space-y-4">
              <div>
                <h3 className="font-medium">{t(category.titleKey)}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t(category.descriptionKey)}</p>
                {!category.requiresConsent && (
                  <span className="inline-block mt-2 text-xs bg-muted px-2 py-1 rounded">
                    {t(
                      'cookiePolicy.alwaysActive',
                      'Always active - Required for website operation'
                    )}
                  </span>
                )}
              </div>

              {/* Cookie table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium">
                        {t('cookiePolicy.table.cookie', 'Cookie')}
                      </th>
                      <th className="text-left py-2 pr-4 font-medium">
                        {t('cookiePolicy.table.provider', 'Provider')}
                      </th>
                      <th className="text-left py-2 pr-4 font-medium">
                        {t('cookiePolicy.table.purpose', 'Purpose')}
                      </th>
                      <th className="text-left py-2 font-medium">
                        {t('cookiePolicy.table.duration', 'Duration')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.cookies.map((cookie) => (
                      <tr key={cookie.name} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-mono text-xs">{cookie.name}</td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {cookie.provider || '-'}
                        </td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {cookie.description[i18n.language] || cookie.description.en}
                        </td>
                        <td className="py-2 text-muted-foreground">{cookie.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>

        {/* How to manage cookies */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t('cookiePolicy.manageCookies.title', 'How to manage cookies')}
          </h2>
          <p className="text-muted-foreground">
            {t(
              'cookiePolicy.manageCookies.description',
              'You can change your cookie preferences at any time by clicking the button below or using the "Cookie settings" link in the footer of our website.'
            )}
          </p>
          <div className="flex justify-end">
            <Button onClick={openSettings}>
              {t('cookiePolicy.manageCookies.button', 'Manage cookie settings')}
            </Button>
          </div>
        </section>

        {/* Your rights */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t('cookiePolicy.yourRights.title', 'Your rights')}
          </h2>
          <p className="text-muted-foreground">
            {t(
              'cookiePolicy.yourRights.description',
              'Under GDPR, you have the right to access, rectify, or erase your personal data. You also have the right to withdraw your consent at any time. For more information about your rights and how we handle your data, please see our Privacy Policy.'
            )}
          </p>
        </section>

        {/* Contact */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{t('cookiePolicy.contact.title', 'Contact')}</h2>
          <p className="text-muted-foreground">
            {t(
              'cookiePolicy.contact.description',
              'If you have any questions about our cookie policy, please contact us through the contact information provided in our imprint.'
            )}
          </p>
        </section>
      </CardContent>
    </Card>
  )
}
